import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE from '../../api/config';
import { useUser } from '../../context/UserContext';
import { useAppAlert } from '../../components/AppAlerts';

export default function Login() {
    const [step, setStep] = useState('username'); // 'username' | 'password' | 'set-password' | 'register'
    const [username, setUsername] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useUser();
    const notify = useAppAlert();

    const from = location.state?.from?.pathname || '/';

    const showError = (message) => {
        setError(message);
        notify(message, 'error');
    };

    async function handleUsernameSubmit(e) {
        e.preventDefault();
        if (!username.trim()) return showError('Enter a username');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/check-username`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim() }),
            });
            const data = await res.json();
            if (!data.exists) setStep('register');
            else if (!data.hasPassword) setStep('set-password');
            else setStep('password');
        } catch {
            showError('Network error, please try again');
        } finally {
            setLoading(false);
        }
    }

    async function handleLoginSubmit(e) {
        e.preventDefault();
        if (!password) return showError('Enter your password');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password }),
            });
            const data = await res.json();
            if (!res.ok) return showError(data.error || 'Login failed');
            login(data.user, data.token);
            navigate(from, { replace: true });
        } catch {
            showError('Network error, please try again');
        } finally {
            setLoading(false);
        }
    }

    async function handleRegisterSubmit(e) {
        e.preventDefault();
        if (!password) return showError('Enter a password');
        if (password.length < 6) return showError('Password must be at least 6 characters');
        if (password !== confirmPassword) return showError('Passwords do not match');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), display_name: displayName.trim() || username.trim(), password }),
            });
            const data = await res.json();
            if (!res.ok) return showError(data.error || 'Registration failed');
            login(data.user, data.token);
            navigate(from, { replace: true });
        } catch {
            showError('Network error, please try again');
        } finally {
            setLoading(false);
        }
    }

    function goBack() {
        setStep('username');
        setPassword('');
        setConfirmPassword('');
        setError('');
    }

    async function handleSetPasswordSubmit(e) {
        e.preventDefault();
        if (!password) return showError('Enter a password');
        if (password.length < 6) return showError('Password must be at least 6 characters');
        if (password !== confirmPassword) return showError('Passwords do not match');
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/auth/set-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password }),
            });
            const data = await res.json();
            if (!res.ok) return showError(data.error || 'Failed to set password');
            login(data.user, data.token);
            navigate(from, { replace: true });
        } catch {
            showError('Network error, please try again');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="login-page">
            {step === 'username' && (
                <>
                    <h3 className="mb-4">Who are you?</h3>
                    <form onSubmit={handleUsernameSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Username</label>
                            <input
                                className="form-control"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                placeholder="Enter your username"
                                autoFocus
                            />
                        </div>
                        <button className="btn btn-primary w-100" disabled={loading}>
                            {loading ? 'Checking...' : 'Continue'}
                        </button>
                    </form>
                </>
            )}

            {step === 'password' && (
                <>
                    <h3 className="mb-1">Welcome back</h3>
                    <p className="text-muted mb-4">Signing in as <strong>{username}</strong></p>
                    <form onSubmit={handleLoginSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoFocus
                            />
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-secondary" onClick={goBack}>Back</button>
                            <button className="btn btn-primary flex-grow-1" disabled={loading}>
                                {loading ? 'Signing in...' : 'Sign In'}
                            </button>
                        </div>
                    </form>
                </>
            )}

            {step === 'set-password' && (
                <>
                    <h3 className="mb-1">Set your password</h3>
                    <p className="text-muted mb-4">Account found for <strong>{username}</strong> — no password set yet</p>
                    <form onSubmit={handleSetPasswordSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Min. 6 characters"
                                autoFocus
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Confirm Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Repeat password"
                            />
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-secondary" onClick={goBack}>Back</button>
                            <button className="btn btn-primary flex-grow-1" disabled={loading}>
                                {loading ? 'Saving...' : 'Set Password'}
                            </button>
                        </div>
                    </form>
                </>
            )}

            {step === 'register' && (
                <>
                    <h3 className="mb-1">Create account</h3>
                    <p className="text-muted mb-4">New account for <strong>{username}</strong></p>
                    <form onSubmit={handleRegisterSubmit}>
                        <div className="mb-3">
                            <label className="form-label">Display Name <span className="text-muted fw-normal">(optional)</span></label>
                            <input
                                className="form-control"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                placeholder={username}
                                autoFocus
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                placeholder="Min. 6 characters"
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Confirm Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Repeat password"
                            />
                        </div>
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-secondary" onClick={goBack}>Back</button>
                            <button className="btn btn-primary flex-grow-1" disabled={loading}>
                                {loading ? 'Creating account...' : 'Create Account'}
                            </button>
                        </div>
                    </form>
                </>
            )}
        </div>
    );
}
